import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useAppSubmitting } from "../../hooks/useAppSubmitting";
import { useUser, useUserCredits } from "../../hooks/useAuth";
import { usePartyGameByShellId } from "../../hooks/usePartyCatalog";
import { partyShellIdToModePathSegment } from "../../modes/shellToModeApi";
import { PartyAccessBadge, PartyCoinBadge, PartyTopBar } from "../../party/PartyChrome";
import { PartyMascot } from "../../party/PartyMascot";
import { PartyPlayFormatChips } from "../../party/PartyPlayFormatChips";
import { partyShellSupportsTrial } from "../../party/partyTrialMockData";
import { PartyTvCastGlyph } from "../../party/PartyTvCastGlyph";
import { usePartyShellGames } from "../../party/usePartyShellGames";
import { apiClient } from "../../utils/api";
import { MixpanelEvents, trackEvent } from "../../utils/mixpanel";
import { modeCategoryDisplayName } from "../../utils/modeCategoryLabels";

const startBtnInnerSx = {
  position: "relative" as const,
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.25,
};

function toneLabel(tone: string): string {
  if (tone === "mystery") return "خداع";
  if (tone === "competitive") return "تنافسي";
  if (tone === "premium") return "هادئ";
  return "فوضوي";
}

function readImposterHowSteps(
  t: (key: string, options?: Record<string, unknown>) => unknown,
  key: "howOnlineSteps" | "howGatheringSteps",
): string[] {
  const raw = t(`party.detail.imposter.${key}`, { returnObjects: true });
  return Array.isArray(raw) ? raw.map((x) => String(x)) : [];
}

type CatalogPreview =
  | { kind: "categories"; segment: string }
  | { kind: "topics" }
  | null;

function catalogPreviewForShell(shellId: string | undefined): CatalogPreview {
  if (!shellId) return null;
  if (shellId === "category") return null;
  if (shellId === "challenge") return null;
  return { kind: "categories", segment: partyShellIdToModePathSegment(shellId) };
}

export default function PartyDetailPage() {
  const { shellId } = useParams<{ shellId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data: user } = useUser();
  const creditsProfileQ = useUserCredits();
  const creditBalance = creditsProfileQ.data?.credit ?? 0;
  const noRestrictions = creditsProfileQ.data?.no_restrictions === true;
  const { isAppSubmitting } = useAppSubmitting();
  const { credits } = usePartyShellGames();
  const detailQ = usePartyGameByShellId(shellId);
  const g = detailQ.data;

  const catalogPreview = useMemo(() => catalogPreviewForShell(shellId), [shellId]);

  const categoriesPreviewQ = useQuery({
    queryKey: ["party", "detail", "mode-categories", catalogPreview?.kind === "categories" ? catalogPreview.segment : ""],
    queryFn: () => {
      if (catalogPreview?.kind !== "categories") return Promise.resolve([]);
      return apiClient.getModeCategories(catalogPreview.segment);
    },
    enabled: catalogPreview?.kind === "categories",
    staleTime: 5 * 60 * 1000,
  });

  const topicsPreviewQ = useQuery({
    queryKey: ["party", "detail", "category-topics"],
    queryFn: () => apiClient.getCategoryGameTopics(),
    enabled: catalogPreview?.kind === "topics",
    staleTime: 5 * 60 * 1000,
  });

  const displayHowSteps = useMemo(() => {
    if (g?.id === "imposter-online") return readImposterHowSteps(t, "howOnlineSteps");
    return g?.how ?? [];
  }, [g?.id, g?.how, t]);

  useEffect(() => {
    if (!g) return;
    trackEvent(MixpanelEvents.PARTY_DETAIL_VIEWED, {
      shell_id: g.id,
      access: g.access,
      is_paid: g.access === "paid",
    });
  }, [g]);

  if (!shellId) return null;

  if (detailQ.isError || (!detailQ.isLoading && !g)) {
    return (
      <Box sx={{ p: 3 }}>
        <PartyTopBar title="—" onBack={() => navigate("/party/modes")} />
        <Typography sx={{ color: "var(--ink-dim)" }}>اللعبة غير موجودة.</Typography>
      </Box>
    );
  }

  if (detailQ.isLoading && !g) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "var(--ink-dim)" }}>
        …
      </Box>
    );
  }

  if (!g) return null;

  const needsPurchase = g.access === "paid";

  const goUnlock = () => {
    trackEvent(MixpanelEvents.PARTY_UNLOCK_CLICKED, {
      shell_id: g.id,
      logged_in: Boolean(user),
    });
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/purchase")}`);
      return;
    }
    navigate("/purchase");
  };

  const assertCanSpendCredit = () => {
    if (!user) {
      trackEvent(MixpanelEvents.PARTY_PLAY_LOGIN_REQUIRED, { shell_id: g.id });
      navigate(`/login?next=${encodeURIComponent(`/party/play/${g.id}`)}`);
      return false;
    }
    if (g.consumesCredit && !noRestrictions && creditBalance < 1) {
      trackEvent(MixpanelEvents.PARTY_GAME_CREDITS_BLOCKED, {
        shell_id: g.id,
        context: "detail_start_navigate_purchase",
        balance: creditBalance,
      });
      navigate("/purchase");
      return false;
    }
    return true;
  };

  const goPlay = () => {
    if (!assertCanSpendCredit()) return;
    trackEvent(MixpanelEvents.PARTY_PLAY_CLICKED, {
      shell_id: g.id,
      access: g.access,
      trial: false,
    });
    navigate(`/party/play/${g.id}`);
  };

  const goImposterOnline = (flow: "private" | "public") => {
    if (!assertCanSpendCredit()) return;
    trackEvent(MixpanelEvents.PARTY_PLAY_CLICKED, {
      shell_id: g.id,
      access: g.access,
      trial: false,
      imposter_online_flow: flow,
    });
    navigate(`/party/play/imposter-online?onlineFlow=${flow}`);
  };

  /** Trial: only modes that use credits (not free), never bluff / locked; hidden during App Store review build. */
  const showTrialEntry =
    g.consumesCredit && partyShellSupportsTrial(g.id) && g.access !== "locked" && !isAppSubmitting;

  const goTrial = () => {
    const path = `/party/play/${g.id}?trial=1`;
    trackEvent(MixpanelEvents.PARTY_TRIAL_ENTRY_CLICKED, {
      shell_id: g.id,
      access: g.access,
      requires_login: !user,
    });
    if (!user) {
      trackEvent(MixpanelEvents.PARTY_PLAY_LOGIN_REQUIRED, { shell_id: g.id, trial: true });
      navigate(`/login?next=${encodeURIComponent(path)}`);
      return;
    }
    trackEvent(MixpanelEvents.PARTY_PLAY_CLICKED, {
      shell_id: g.id,
      access: g.access,
      trial: true,
    });
    navigate(path);
  };

  const demoWord = g.demo?.word;
  const demoForbidden = g.demo?.forbidden ?? [];

  const cardCssVar = { ["--card-bg" as string]: g.bg } as const;

  return (
    <Box sx={{ pb: 17.5 }}>
      <PartyTopBar
        title={g.nameAr.toUpperCase()}
        onBack={() => navigate("/party/modes")}
        right={<PartyCoinBadge amount={credits} />}
      />

      <Box
        className="party-kan-card party-anim-card-deal"
        sx={{
          ...cardCssVar,
          margin: "4px 20px 0",
          padding: "24px 22px",
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            <PartyAccessBadge access={g.access} price={g.price} />
            <Box component="span" className="party-chip">
              {g.playersTag}
            </Box>
            <Box component="span" className="party-chip" sx={{ display: "inline-flex", px: 1, py: 0.5, lineHeight: 0 }}>
              <PartyTvCastGlyph support={g.supportsTvCast} />
            </Box>
            <PartyPlayFormatChips format={g.playFormat} />
          </Stack>
          <Typography component="div" className="party-display" sx={{ fontSize: 56, lineHeight: 0.88 }}>
            {g.nameAr}
          </Typography>
          <Typography sx={{ mt: 1.25, color: "var(--ink-dim)", fontSize: 14, maxWidth: "26ch" }}>{g.desc}</Typography>
        </Box>
        <PartyMascot shellId={g.id} size={120} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 1,
          py: 2,
          px: 2.5,
          pt: 2,
          pb: 0.5,
        }}
      >
        {[
          { k: "اللاعبون", v: g.players },
          { k: "الوقت", v: g.time },
          { k: "الوضع", v: toneLabel(g.tone) },
        ].map((s, i) => (
          <Box
            key={i}
            sx={{
              border: "1.5px solid var(--ink-faint)",
              borderRadius: 2,
              py: 1.5,
              px: 1.25,
              textAlign: "center",
            }}
          >
            <Typography className="party-mono" sx={{ fontSize: 10, color: "var(--ink-dim)", letterSpacing: "0.1em" }}>
              {s.k}
            </Typography>
            <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 16, mt: 0.5 }}>{s.v}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 2.5, pt: 2.25, pb: 1 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography
            component="h3"
            sx={{ m: 0, fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 900 }}
          >
            {g.id === "imposter" || g.id === "imposter-online" ? t("party.detail.imposter.howToTitle") : "طريقة اللعب"}
          </Typography>
        </Stack>
        <Stack component="ol" spacing={1.25} sx={{ listStyle: "none", p: 0, m: 0 }}>
          {displayHowSteps.map((step, i) => (
            <Stack
              component="li"
              key={i}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              sx={{
                p: "12px 14px",
                borderRadius: 2,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--ink-faint)",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  background: "var(--hot)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--font-display)",
                  fontWeight: 900,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {["١", "٢", "٣", "٤"][i] ?? "٥"}
              </Box>
              <Typography sx={{ fontSize: 15, lineHeight: 1.55 }}>{step}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {catalogPreview ? (
        <Box sx={{ px: 2.5, pt: 0.5, pb: 1.5 }}>
          <details className="party-detail-catalog">
            <summary className="party-detail-catalog-summary">
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ width: 1 }}>
                <Typography
                  component="span"
                  className="party-display"
                  sx={{ fontSize: 17, fontWeight: 900, lineHeight: 1.2, color: "var(--ink)", textAlign: "start" }}
                >
                  {catalogPreview.kind === "topics"
                    ? t("party.detail.topicsExploreTitle")
                    : t("party.detail.categoriesExploreTitle")}
                </Typography>
                <Box component="span" className="party-detail-catalog-chevron" aria-hidden>
                  ▼
                </Box>
              </Stack>
              <Typography
                component="span"
                className="party-mono"
                sx={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--ink-dim)", letterSpacing: "0.06em", lineHeight: 1.45 }}
              >
                {t("party.detail.categoriesExploreHint")}
              </Typography>
            </summary>
            <Box className="party-detail-catalog-panel" sx={{ px: 2, pb: 2 }}>
              {catalogPreview.kind === "categories" ? (
                <>
                  {categoriesPreviewQ.isLoading ? (
                    <Typography sx={{ mt: 1.5, color: "var(--ink-dim)", fontSize: 14 }}>{t("party.detail.categoriesLoading")}</Typography>
                  ) : categoriesPreviewQ.isError ? (
                    <Typography sx={{ mt: 1.5, color: "var(--hot)", fontSize: 14 }}>{t("party.detail.categoriesLoadError")}</Typography>
                  ) : (categoriesPreviewQ.data?.length ?? 0) === 0 ? (
                    <Typography sx={{ mt: 1.5, color: "var(--ink-dim)", fontSize: 14 }}>{t("party.detail.categoriesEmpty")}</Typography>
                  ) : (
                    <Stack component="ul" spacing={1} sx={{ listStyle: "none", p: 0, m: "12px 0 0" }}>
                      {(categoriesPreviewQ.data ?? []).map((c) => (
                        <Box
                          component="li"
                          key={c.id}
                          sx={{
                            py: 1.25,
                            px: 1.5,
                            borderRadius: 1.5,
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid var(--ink-faint)",
                            fontSize: 15,
                            lineHeight: 1.45,
                          }}
                        >
                          {modeCategoryDisplayName(c, i18n.language)}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              ) : (
                <>
                  {topicsPreviewQ.isLoading ? (
                    <Typography sx={{ mt: 1.5, color: "var(--ink-dim)", fontSize: 14 }}>{t("party.detail.categoriesLoading")}</Typography>
                  ) : topicsPreviewQ.isError ? (
                    <Typography sx={{ mt: 1.5, color: "var(--hot)", fontSize: 14 }}>{t("party.detail.categoriesLoadError")}</Typography>
                  ) : (topicsPreviewQ.data?.length ?? 0) === 0 ? (
                    <Typography sx={{ mt: 1.5, color: "var(--ink-dim)", fontSize: 14 }}>{t("party.detail.categoriesEmpty")}</Typography>
                  ) : (
                    <Stack component="ul" spacing={1} sx={{ listStyle: "none", p: 0, m: "12px 0 0" }}>
                      {(topicsPreviewQ.data ?? []).map((row) => (
                        <Box
                          component="li"
                          key={row.id}
                          sx={{
                            py: 1.25,
                            px: 1.5,
                            borderRadius: 1.5,
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid var(--ink-faint)",
                            fontSize: 15,
                            lineHeight: 1.45,
                          }}
                        >
                          {modeCategoryDisplayName(
                            { name_en: row.name_en ?? "", name_ar: row.name_ar ?? "" },
                            i18n.language,
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Box>
          </details>
        </Box>
      ) : null}

      {demoWord && (
        <Box sx={{ py: 1.5, px: 2.5 }}>
          <Typography className="party-mono" sx={{ fontSize: 10, color: "var(--ink-dim)", letterSpacing: "0.1em", mb: 1 }}>
            معاينة البطاقة
          </Typography>
          <Box
            className="party-kan-card"
            sx={{
              ...({ ["--card-bg" as string]: "var(--paper)" } as const),
              color: "var(--paper-ink)",
              p: 2.5,
              textAlign: "center",
            }}
          >
            <Typography
              className="party-mono"
              sx={{
                fontSize: 10,
                letterSpacing: "0.15em",
                color: "color-mix(in oklab, var(--paper-ink) 60%, transparent)",
              }}
            >
              الكلمة
            </Typography>
            <Typography component="div" className="party-display" sx={{ fontSize: 52, my: 0.5, mb: 1.5 }}>
              {demoWord}
            </Typography>
            <Typography
              className="party-mono"
              sx={{
                fontSize: 10,
                letterSpacing: "0.15em",
                color: "color-mix(in oklab, var(--paper-ink) 60%, transparent)",
                mb: 0.75,
              }}
            >
              ممنوعات
            </Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" justifyContent="center" spacing={0.75}>
              {demoForbidden.map((f) => (
                <Box
                  component="span"
                  key={f}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    background: "color-mix(in oklab, var(--paper-ink) 10%, transparent)",
                    color: "var(--paper-ink)",
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: 13,
                    position: "relative",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      left: 6,
                      right: 6,
                      top: "50%",
                      height: 2,
                      background: "var(--hot)",
                      transform: "rotate(-6deg)",
                    }}
                  />
                  {f}
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      <Box
        className="party-detail-actions-footer"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          maxWidth: 520,
          mx: "auto",
          px: 2.5,
          py: 1.75,
          pb: "calc(20px + var(--safe-bot))",
          zIndex: 50,
        }}
      >
        {g.access === "locked" ? (
          <Box component="button" type="button" className="party-btn party-btn-ghost" sx={{ width: 1 }} disabled>
            غير متاح حاليًا
          </Box>
        ) : g.id === "imposter-online" ? (
          <Stack spacing={1.25}>
            {needsPurchase ? (
              <Stack direction="row" spacing={1.25} sx={{ width: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography className="party-mono" sx={{ fontSize: 10, color: "var(--ink-dim)", letterSpacing: "0.08em" }}>
                    فتح اللعبة
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={0.75}>
                    <Typography component="div" className="party-display" sx={{ fontSize: 30, color: "var(--hot)" }}>
                      ₡ {g.price ?? "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--ink-dim)" }}>من المتجر</Typography>
                  </Stack>
                </Box>
                <Box component="button" type="button" onClick={goUnlock} className="party-btn party-btn-hot" sx={{ flex: 1 }}>
                  المتجر / التفعيل
                </Box>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1.25} sx={{ width: 1, alignItems: "stretch" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => goImposterOnline("private")}
                  className="party-btn party-btn-hot"
                  sx={{ flex: 1, minWidth: 0, py: 1.75, px: 1.25, fontSize: 15, position: "relative" }}
                >
                  <Stack sx={{ ...startBtnInnerSx, flexDirection: "column", gap: 0.5 }}>
                    <Box component="span">{t("party.detail.imposter.onlinePrivateCta")}</Box>
                    {g.consumesCredit && !isAppSubmitting ? (
                      <Typography component="span" className="party-mono" sx={{ fontSize: 12, fontWeight: 900, opacity: 0.95 }}>
                        {t("party.detail.imposter.onlineCreditsHint")} · {t("party.detail.startCostBadge")}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
                {noRestrictions ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => goImposterOnline("public")}
                    className="party-btn party-btn-paper"
                    sx={{ flex: 1, minWidth: 0, py: 1.75, px: 1.25, fontSize: 15, position: "relative" }}
                  >
                    <Stack sx={{ ...startBtnInnerSx, flexDirection: "column", gap: 0.5 }}>
                      <Box component="span">{t("party.detail.imposter.onlinePublicCta")}</Box>
                      {g.consumesCredit && !isAppSubmitting ? (
                        <Typography component="span" className="party-mono" sx={{ fontSize: 12, fontWeight: 900, opacity: 0.95 }}>
                          {t("party.detail.imposter.onlineCreditsHint")} · {t("party.detail.startCostBadge")}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                ) : null}
              </Stack>
            )}
          </Stack>
        ) : (
          <Stack spacing={1.25}>
            {showTrialEntry ? (
              <>
                <Typography
                  className="party-display"
                  sx={{
                    fontSize: 12,
                    color: "var(--mint)",
                    textAlign: "center",
                    fontWeight: 900,
                    letterSpacing: "0.02em",
                    lineHeight: 1.3,
                  }}
                >
                  {t("party.trial.eyebrow")}
                </Typography>
                <Stack direction="row" spacing={1.25} alignItems="stretch" sx={{ width: 1 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={goTrial}
                    className="party-btn party-btn-trial"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      py: 1.75,
                      px: 1.25,
                      fontSize: 16,
                      gap: 0.75,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 20, flexShrink: 0 }} aria-hidden />
                    <Typography component="span" sx={{ lineHeight: 1.15, textAlign: "center" }}>
                      {t("party.trial.cta")}
                    </Typography>
                  </Box>
                  {needsPurchase ? (
                    <Stack sx={{ flex: 1, minWidth: 0, gap: 1, justifyContent: "center" }}>
                      <Typography className="party-mono" sx={{ fontSize: 9, color: "var(--ink-dim)", letterSpacing: "0.06em" }}>
                        فتح اللعبة
                      </Typography>
                      <Stack direction="row" alignItems="baseline" spacing={0.5} useFlexGap flexWrap="wrap">
                        <Typography component="div" className="party-display" sx={{ fontSize: 22, color: "var(--hot)", lineHeight: 1 }}>
                          ₡ {g.price ?? "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "var(--ink-dim)" }}>من المتجر</Typography>
                      </Stack>
                      <Box
                        component="button"
                        type="button"
                        onClick={goUnlock}
                        className="party-btn party-btn-hot"
                        sx={{ width: 1, py: 1.75, px: 1.5, fontSize: 16 }}
                      >
                        المتجر / التفعيل
                      </Box>
                    </Stack>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={goPlay}
                      className="party-btn party-btn-hot"
                      sx={{ flex: 1, minWidth: 0, py: 1.75, px: 1.25, fontSize: 17 }}
                    >
                      <Box sx={startBtnInnerSx}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        ابدأ اللعبة
                        {g.consumesCredit && !isAppSubmitting ? (
                          <Typography
                            component="span"
                            className="party-mono"
                            sx={{
                              position: "absolute",
                              insetInlineEnd: 2,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 13,
                              fontWeight: 900,
                              opacity: 0.92,
                              letterSpacing: "0.04em",
                            }}
                          >
                            {t("party.detail.startCostBadge")}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                </Stack>
                <Typography className="party-mono" sx={{ fontSize: 10, color: "var(--ink-dim)", textAlign: "center", lineHeight: 1.45 }}>
                  {t("party.trial.hint")}
                </Typography>
              </>
            ) : needsPurchase ? (
              <Stack direction="row" spacing={1.25} sx={{ width: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography className="party-mono" sx={{ fontSize: 10, color: "var(--ink-dim)", letterSpacing: "0.08em" }}>
                    فتح اللعبة
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={0.75}>
                    <Typography component="div" className="party-display" sx={{ fontSize: 30, color: "var(--hot)" }}>
                      ₡ {g.price ?? "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--ink-dim)" }}>من المتجر</Typography>
                  </Stack>
                </Box>
                <Box component="button" type="button" onClick={goUnlock} className="party-btn party-btn-hot" sx={{ flex: 1 }}>
                  المتجر / التفعيل
                </Box>
              </Stack>
            ) : (
              <Box component="button" type="button" onClick={goPlay} className="party-btn party-btn-hot" sx={{ width: 1 }}>
                <Box sx={startBtnInnerSx}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  ابدأ اللعبة
                  {g.consumesCredit && !isAppSubmitting ? (
                    <Typography
                      component="span"
                      className="party-mono"
                      sx={{
                        position: "absolute",
                        insetInlineEnd: 4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 16,
                        fontWeight: 900,
                        opacity: 0.92,
                        letterSpacing: "0.04em",
                      }}
                    >
                      {t("party.detail.startCostBadge")}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
